// apps/content/content/data/services.ts

import type {
  Service,
  ServicePricing,
  PricingPackage,
  ServiceAddon,
  ServicePalette,
  CtaType,
  HeroType,
} from "@/lib/content-types";

// Re-export types for backward compat with existing imports
export type { Service, ServicePricing, PricingPackage, ServiceAddon, ServicePalette, CtaType, HeroType };

// ─── 1. SITI WORDPRESS ──────────────────────────────────────────────────────

const sitiWordpressPricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "essenziale",
      label: "Sito essenziale",
      priceFrom: 2200,
      deliveryDays: { min: 21, max: 28 },
      includes: [
        "6-8 pagine con design custom (no template)",
        "Impostazioni SEO di base (GA4, Search Console, sitemap)",
        "Mobile-first responsive",
        "Form contatti con anti-spam",
        "1h formazione cliente + 30gg supporto post-lancio",
      ],
    },
    {
      id: "blog-multi",
      label: "Sito + blog o multilingua",
      priceFrom: 2700,
      deliveryDays: { min: 28, max: 35 },
      includes: [
        "Tutto di Essenziale",
        "Blog cluster setup con categorie e tag",
        "1 lingua extra (WPML)",
      ],
      highlight: true,
    },
    {
      id: "pro",
      label: "Sito Pro",
      priceFrom: 3800,
      deliveryDays: { min: 35, max: 42 },
      includes: [
        "Tutto di Blog/Multilingua",
        "Integrazioni CRM/booking (HubSpot, Calendly, Mailchimp)",
        "Landing pages dedicate per campagne",
        "SEO tecnico avanzato (schema, CWV verdi, internal linking strategico)",
      ],
    },
    {
      id: "custom",
      label: "Su misura",
      priceFrom: 5000,
      customQuote: true,
      deliveryDays: { min: 42, max: 90 },
      includes: [
        "Progetto ampio con più referenti",
        "Design system completo",
        "Integrazioni custom (ERP, gestionale, payment custom)",
        "A/B testing + Lighthouse CI",
      ],
    },
  ],
  addons: [
    { label: "Copywriting professionale", priceFrom: 80, priceTo: 150, unit: "/ pagina" },
    { label: "Lingua extra (oltre la prima)", priceFrom: 600, priceTo: 1200, unit: "/ lingua" },
    { label: "Migrazione contenuti da vecchio sito", priceFrom: 400, priceTo: 1200 },
    { label: "Photoshooting in loco (subappalto)", priceFrom: 600, priceTo: 1500 },
    { label: "Integrazione CRM custom", priceFrom: 500, priceTo: 1500 },
    { label: "Animazioni 3D/motion avanzato", priceFrom: 500, priceTo: 2000 },
  ],
  discoveryRequired: false,
  hourlyRate: 110,
  notes: [
    "Hosting NON incluso (consulenza scelta + setup inclusi, costo hosting paga cliente)",
    "Licenze plugin commerciali (es. WPML) fatturate separatamente",
  ],
};

// ─── 2. WORDPRESS PERFORMANCE ───────────────────────────────────────────────

const wordpressPerformancePricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "audit",
      label: "Analisi + report",
      priceFrom: 600,
      deliveryDays: { min: 2, max: 3 },
      includes: [
        "Analisi CWV, PageSpeed, GSC, Lighthouse",
        "Report con priorità e stima degli interventi",
      ],
    },
    {
      id: "fix-standard",
      label: "Analisi + interventi",
      priceFrom: 1500,
      deliveryDays: { min: 7, max: 14 },
      includes: [
        "Analisi completa",
        "Configurazione e messa a punto di WP Rocket",
        "Ottimizzazione immagini (formati WebP e AVIF)",
        "Pulizia del database e dei plugin",
        "CDN base",
      ],
    },
    {
      id: "fix-migration",
      label: "Analisi + interventi + trasloco",
      priceFrom: 2800,
      deliveryDays: { min: 14, max: 21 },
      includes: [
        "Tutto di Fix Standard",
        "Migrazione hosting performante (Kinsta / SiteGround / ManagedServer)",
        "CDN avanzato (Cloudflare Pro)",
        "Critical CSS",
      ],
    },
    {
      id: "care-trimestrale",
      label: "Manutenzione prestazioni (trimestrale)",
      priceFrom: 800,
      unit: "/ trimestre",
      deliveryDays: { min: 0, max: 0 },
      includes: [
        "1 analisi con interventi e report ogni 3 mesi",
        "4 interventi all'anno",
      ],
      highlight: true,
    },
    {
      id: "pro-mensile",
      label: "Controllo prestazioni (mensile)",
      priceFrom: 250,
      unit: "/ mese",
      deliveryDays: { min: 0, max: 0 },
      includes: [
        "Controllo automatico delle prestazioni",
        "Ottimizzazione del database mensile",
        "Avvisi prestazioni in tempo reale",
      ],
    },
  ],
  addons: [
    { label: "Riordino del tema poco performante", priceFrom: 500, priceTo: 2000 },
    { label: "Configurazione CDN avanzata (Cloudflare Pro/Business)", priceFrom: 200, priceTo: 500 },
  ],
  discoveryRequired: false,
  hourlyRate: 110,
  notes: [
    "Costo licenze plugin (WP Rocket 59 euro/anno) a carico cliente",
    "Costo hosting nuovo (se migration) pagato direttamente dal cliente",
  ],
};

// ─── 3. ECOMMERCE WOOCOMMERCE ───────────────────────────────────────────────

const ecommerceWoocommercePricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "essenziale",
      label: "Negozio essenziale",
      priceFrom: 5000,
      deliveryDays: { min: 35, max: 42 },
      includes: [
        "Tema custom ottimizzato",
        "Fino 50 prodotti",
        "Gateway multipli (Stripe + PayPal)",
        "Spedizioni dinamiche (configurazione corrieri)",
        "GA4 ecommerce + Enhanced Conversions",
        "SEO base, pagine legali GDPR",
      ],
    },
    {
      id: "mid",
      label: "Negozio medio",
      priceFrom: 7500,
      deliveryDays: { min: 49, max: 56 },
      includes: [
        "Tutto di Essenziale",
        "50-200 prodotti",
        "Multilingua (WPML) e più valute",
        "Automazioni email (Mailchimp / Klaviyo)",
        "Integrazione gestionale base",
      ],
      highlight: true,
    },
    {
      id: "b2b",
      label: "Negozio B2B",
      priceFrom: 12000,
      deliveryDays: { min: 70, max: 98 },
      includes: [
        "Tutto di Mid",
        "B2BKing + listini per ruolo",
        "Preventivi online",
        "Area riservata clienti B2B",
        "Sincronizzazione col gestionale (ERP)",
      ],
    },
    {
      id: "enterprise",
      label: "Su misura",
      priceFrom: 20000,
      customQuote: true,
      deliveryDays: { min: 90, max: 180 },
      includes: [
        "Configuratori prodotto custom",
        "Headless WooCommerce opzionale",
        "Più magazzini e flussi di lavoro su misura",
      ],
    },
  ],
  addons: [
    { label: "Caricamento prodotti oltre 20", priceFrom: 5, priceTo: 15, unit: "/ prodotto" },
    { label: "Foto prodotto styling/ottimizzazione", priceFrom: 15, priceTo: 40, unit: "/ prodotto" },
    { label: "Integrazione corriere custom", priceFrom: 400, priceTo: 1200 },
    { label: "Integrazione ERP/gestionale", priceFrom: 1500, priceTo: 5000 },
    { label: "Configuratore prodotto custom", priceFrom: 2000, priceTo: 8000 },
    { label: "Multilingua oltre 1 lingua", priceFrom: 1000, priceTo: 2500, unit: "/ lingua" },
    { label: "Migrazione da Shopify/Magento", priceFrom: 1500, priceTo: 4000 },
    { label: "Loyalty program / wishlist avanzato", priceFrom: 600, priceTo: 1500 },
  ],
  discoveryRequired: false,
  discoveryPrice: 800,
  discoveryNote: "Obbligatoria per progetti sopra 10.000 euro o B2B complessi. Scontata 100% se procedi entro 30gg.",
  hourlyRate: 110,
  notes: [
    "Plugin commerciali erodono margine (WPML 99-299 euro, B2BKing 139 euro): soglia 5.000 euro non negoziabile",
  ],
};

// ─── 4. SVILUPPO CUSTOM NEXT.JS ─────────────────────────────────────────────

const sviluppoCustomNextjsPricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "spa",
      label: "App web essenziale",
      priceFrom: 4500,
      deliveryDays: { min: 21, max: 28 },
      includes: [
        "Interfaccia su misura (Next.js)",
        "1 server semplice",
        "Messa online su Vercel, con anteprime",
        "Senza database complesso",
      ],
    },
    {
      id: "app-feature-rich",
      label: "App web completa",
      priceFrom: 8000,
      deliveryDays: { min: 42, max: 56 },
      includes: [
        "Interfaccia, server e database (Neon Postgres)",
        "Accessi utente (Clerk o Auth.js)",
        "2-3 integrazioni con servizi esterni",
      ],
      highlight: true,
    },
    {
      id: "piattaforma",
      label: "Piattaforma",
      priceFrom: 15000,
      deliveryDays: { min: 70, max: 98 },
      includes: [
        "Interfaccia, server, database e accessi utente",
        "Pannello di amministrazione su misura",
        "Integrazioni multiple",
        "Monitoraggio e avvisi",
      ],
    },
    {
      id: "enterprise",
      label: "Su misura, grande scala",
      priceFrom: 25000,
      customQuote: true,
      deliveryDays: { min: 90, max: 180 },
      includes: [
        "Progetto grande, più referenti",
        "Diagnosi e piano strutturato",
      ],
    },
  ],
  addons: [
    { label: "Accessi multi-azienda con ruoli", priceFrom: 2000, priceTo: 4000 },
    { label: "Real-time (WebSockets/Pusher)", priceFrom: 1500, priceTo: 3000 },
    { label: "Integrazione AI (ricerca documenti, agenti)", priceFrom: 3000, priceTo: 8000 },
    { label: "Pagamenti recurring (Stripe Subscriptions)", priceFrom: 1500, priceTo: 3000 },
    { label: "Pannello di amministrazione su misura", priceFrom: 2000, priceTo: 5000 },
    { label: "Mobile-first PWA", priceFrom: 800, priceTo: 2000 },
  ],
  discoveryRequired: true,
  discoveryPrice: 1000,
  discoveryNote: "Obbligatoria per progetti 15.000 euro+. Scontata 100% se procedi entro 30gg.",
  hourlyRate: 110,
  notes: [
    "Hosting Vercel + DB (Neon) + servizi 3rd party pagati direttamente dal cliente",
    "Modulare per fase: progetti grandi divisi in sprint da 4-6k cadauno",
  ],
};

// ─── 5. MVP SAAS ────────────────────────────────────────────────────────────

const mvpSaasPricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "starter",
      label: "MVP iniziale",
      priceFrom: 4800,
      deliveryDays: { min: 28, max: 42 },
      includes: [
        "call conoscitiva e definizione del perimetro",
        "Schema dei flussi principali",
        "La funzione principale (1) funzionante",
        "Accessi utente e messa online",
        "Landing page MVP",
        "Documentazione e passaggio di consegne",
      ],
      highlight: true,
    },
    {
      id: "plus",
      label: "MVP esteso",
      priceFrom: 8500,
      deliveryDays: { min: 42, max: 63 },
      includes: [
        "Tutto di Starter",
        "2-3 feature aggiuntive",
        "Pannello di amministrazione base",
        "Percorso di primo accesso",
      ],
    },
    {
      id: "pro",
      label: "MVP completo",
      priceFrom: 14000,
      deliveryDays: { min: 63, max: 84 },
      includes: [
        "Tutto di Plus",
        "Integrazioni payment (Stripe)",
        "Gestione utenti su più livelli",
        "Analytics + email automation",
      ],
    },
  ],
  addons: [
    { label: "Stripe Subscriptions integration", priceFrom: 1500, priceTo: 3000 },
    { label: "Invio email automatiche (Resend)", priceFrom: 800, priceTo: 2000 },
    { label: "Pannello di amministrazione su misura", priceFrom: 2000, priceTo: 5000 },
  ],
  discoveryRequired: true,
  discoveryPrice: 600,
  discoveryNote: "Obbligatoria per scope ambiguo o cliente non-tech. Scontata 100% se procedi entro 30gg.",
  hourlyRate: 110,
  notes: [
    "MVP NON è prodotto pronto vendita: focus su validazione, non scale",
    "Post-MVP: retainer mensile (da 4.800 euro/mese) per iterazione",
  ],
};

// ─── 6. BUG FIXING AI/NO-CODE ───────────────────────────────────────────────

const bugFixingAiPricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "discovery-audit",
      label: "Diagnosi iniziale (obbligatoria)",
      priceFrom: 600,
      deliveryDays: { min: 3, max: 4 },
      includes: [
        "Code review progetto Lovable/Bubble/v0/FlutterFlow",
        "Mappa dei problemi con priorità",
        "Stima degli interventi, con perimetro chiaro",
        "Report concreto e pratico",
      ],
      highlight: true,
    },
    {
      id: "rescue-base",
      label: "Intervento base",
      priceFrom: 2000,
      deliveryDays: { min: 7, max: 14 },
      includes: [
        "Fix bug critici",
        "Sistemazione auth + env vars",
        "Gestione degli errori",
        "Messa online stabile",
      ],
    },
    {
      id: "rescue-refactor",
      label: "Intervento con riordino del codice",
      priceFrom: 4500,
      deliveryDays: { min: 21, max: 28 },
      includes: [
        "Tutto di Intervento base",
        "Riarchitettura moduli critici",
        "Code quality + lint setup",
        "Test base",
      ],
    },
    {
      id: "rebuild",
      label: "Ricostruzione da zero",
      priceFrom: 8000,
      deliveryDays: { min: 28, max: 56 },
      includes: [
        "Riscrittura completa con tecnologie solide (Next.js o WordPress)",
        "Design system minimal",
        "Database proper",
      ],
    },
  ],
  addons: [],
  discoveryRequired: true,
  discoveryPrice: 600,
  discoveryNote: "Discovery obbligatoria sempre. Non scontata (paga la diagnosi, è lavoro reale).",
  hourlyRate: 110,
  notes: [
    "Modello discovery-paid → fixed-scope. Evita hourly puro: incentivi disallineati.",
  ],
};

// ─── 7. AUTOMAZIONI N8N + AI ────────────────────────────────────────────────

const automazioniN8nAiPricing: ServicePricing = {
  publishPrices: true,
  packages: [
    {
      id: "discovery-roadmap",
      label: "Diagnosi e piano",
      priceFrom: 500,
      deliveryDays: { min: 2, max: 3 },
      includes: [
        "Analisi dei processi del cliente",
        "Individuazione dei flussi di lavoro",
        "Priorità e perimetro",
      ],
    },
    {
      id: "workflow-singolo",
      label: "Un flusso di lavoro",
      priceFrom: 1500,
      deliveryDays: { min: 7, max: 14 },
      includes: [
        "1 flusso di lavoro complesso",
        "Collegato a 3-5 programmi",
        "Documentazione",
      ],
    },
    {
      id: "setup-completo",
      label: "Avvio + 3-5 flussi di lavoro",
      priceFrom: 3500,
      deliveryDays: { min: 21, max: 28 },
      includes: [
        "Installazione su un server tuo, protetta",
        "3-5 flussi di lavoro operativi",
        "Formazione cliente",
      ],
      highlight: true,
    },
    {
      id: "integrazione-ai",
      label: "Integrazione AI",
      priceFrom: 5000,
      deliveryDays: { min: 28, max: 42 },
      includes: [
        "AI che legge e prepara testi per te",
        "Risposte basate sui tuoi documenti",
        "Avvisi se qualcosa va storto",
      ],
    },
    {
      id: "automation-care",
      label: "Manutenzione automazioni (abbonamento)",
      priceFrom: 600,
      unit: "/ mese (fino a 1200)",
      deliveryDays: { min: 0, max: 0 },
      includes: [
        "Server e controllo continuo",
        "Messa a punto continua",
        "1-2 nuovi flussi di lavoro al mese",
      ],
    },
  ],
  addons: [],
  discoveryRequired: true,
  discoveryPrice: 500,
  discoveryNote: "Discovery obbligatoria sempre. Scontata 100% se procedi entro 30gg.",
  hourlyRate: 110,
  notes: [
    "Costo API AI (OpenAI, Anthropic) a carico cliente",
    "Hosting n8n cloud (20+ euro/mese) opzionale pagato da cliente",
  ],
};

// ─── 8. SITI 3D CREATIVE ────────────────────────────────────────────────────

const siti3dCreativePricing: ServicePricing = {
  publishPrices: false, // No public pricing finché primo progetto fatto
  packages: [
    {
      id: "founding-partner",
      label: "Cliente fondatore (riservato)",
      priceFrom: 3500,
      customQuote: true,
      deliveryDays: { min: 28, max: 56 },
      includes: [
        "Sconto 30-40% per portfolio + testimonial",
        "Concept + modellazione Spline/R3F",
        "Integrazione nel sito responsive + performance",
      ],
    },
  ],
  addons: [],
  discoveryRequired: true,
  discoveryNote: "call conoscitiva gratuita per stimare progetto 3D.",
  hourlyRate: 110,
  notes: [
    "Pagina pubblica vende capability + processo + showcase Spline live, no listino prezzi",
    "Primi 2 clienti founding partner; da cliente 3 in poi prezzi mercato standard 7.000-20.000+ euro",
  ],
};

// ─── 9. MANUTENZIONE EVOLUTIVA ──────────────────────────────────────────────

const manutenzioneEvolutivaPricing: ServicePricing = {
  publishPrices: false, // Rimanda a /abbonamento per pricing
  packages: [],
  addons: [],
  discoveryRequired: false,
  hourlyRate: 110,
  notes: [
    "Master servizio educativo/SEO. Pagina pubblica link a /abbonamento per pricing e attivazione",
  ],
};

// ─── SERVICES ARRAY ─────────────────────────────────────────────────────────

export const SERVICES: Service[] = [
  {
    slug: "siti-wordpress",
    name: "Siti WordPress",
    shortLabel: "Siti WordPress",
    tagline: "Siti custom WordPress per PMI: veloci, SEO-ready, senza template riciclati",
    seoTitle: "Siti WordPress a Bergamo, da 2200 euro",
    h1: "Siti WordPress su misura per PMI",
    metaDescription: "Siti WordPress su misura per PMI di Bergamo e provincia: veloci, facili da aggiornare, pensati per portarti contatti. Online in 3-6 settimane.",
    category: "wordpress",
    isLocalRelevant: true,
    defaultCta: "contact",
    defaultHero: "wordpress-creation",
    pricing: sitiWordpressPricing,
    palette: { primary: "hsl(200, 80%, 50%)", accent: "hsl(180, 78%, 52%)" },
    deliveryDays: { min: 21, max: 42 },
    processSteps: [
      { name: "Analisi e strategia", text: "Analisi obiettivi commerciali, competitor, keyword, stato attuale." },
      { name: "Struttura ed esperienza d'uso", text: "Mappa del sito, schema delle pagine e percorsi di conversione, prima del design." },
      { name: "Design e sviluppo", text: "Design custom brand-aligned e sviluppo WordPress con performance native." },
      { name: "Ottimizzazione per Google", text: "Meta, schema markup, internal linking, Core Web Vitals verdi." },
      { name: "Pubblicazione e consegna", text: "Messa online, test, formazione e monitoraggio dei primi 30 giorni." },
    ],
    usualObjections: [
      "WordPress è lento",
      "Non voglio un template",
      "Il mio sito attuale è troppo vecchio da recuperare",
    ],
    aeoAnswer: "Un sito WordPress custom per PMI costa da 2.200 euro e si realizza in 3-6 settimane. È un sito su misura, non template, pensato per convertire visite in clienti. Tre fasi: discovery (analisi obiettivi e competitor), build (design + sviluppo + SEO setup), launch (deploy + formazione + monitoring 30 giorni). Stack: WordPress core + tema custom, no page builder pesanti, performance Core Web Vitals verdi.",
    affinitySlug: "wordpress-performance",
    affinityAlt: "ecommerce-woocommerce",
    tags: ["wordpress", "creation", "pmi", "local"],
  },
  {
    slug: "wordpress-performance",
    name: "WordPress Performance",
    shortLabel: "WP Performance",
    tagline: "Da siti lenti a Core Web Vitals verdi in 2-3 settimane",
    seoTitle: "Velocizzare un sito WordPress, da 600 euro",
    h1: "Velocizzare un sito WordPress e portare i Core Web Vitals in verde",
    metaDescription: "Il tuo sito WordPress è lento? Trovo le cause reali e lo rendo veloce in 2-3 settimane, con i numeri prima e dopo per vedere la differenza.",
    category: "wordpress",
    isLocalRelevant: true,
    defaultCta: "contact",
    defaultHero: "wordpress-performance",
    pricing: wordpressPerformancePricing,
    palette: { primary: "hsl(170, 85%, 45%)", accent: "hsl(90, 75%, 58%)" },
    deliveryDays: { min: 3, max: 21 },
    processSteps: [
      { name: "Analisi iniziale", text: "Misuro velocità e stabilità con gli strumenti di Google (PageSpeed e Search Console) e trovo cosa rallenta il sito." },
      { name: "Piano di intervento", text: "Priorità degli interventi in base all'impatto e al costo." },
      { name: "Ottimizzazione pratica", text: "Intervengo sulle cause vere: immagini, codice superfluo, cache, database e distribuzione dei contenuti." },
      { name: "Verifica risultati", text: "Misuro di nuovo, anche con i dati dei visitatori reali, e ti consegno un report chiaro con il prima e il dopo." },
      { name: "Controllo continuo", text: "Con il controllo periodico, se un aggiornamento rallenta di nuovo il sito ricevo un avviso e intervengo." },
    ],
    usualObjections: [
      "Ho già provato con un plugin di cache",
      "Mi hanno detto che il tema è il problema",
      "Ogni aggiornamento mi peggiora le cose",
    ],
    aeoAnswer: "Un sito WordPress lento perde visite e contatti. Parto da un'analisi con report (600 euro, 2-3 giorni) che trova le cause vere: immagini, cache, plugin, hosting, database. Poi intervengo: da 1500 euro per gli interventi, da 2800 se serve anche il trasloco su un hosting più veloce, in 1-3 settimane. Alla fine ti consegno i numeri misurati prima e dopo. Per non tornare lenti c'è il controllo trimestrale da 800 euro.",
    affinitySlug: "siti-wordpress",
    affinityAlt: "ecommerce-woocommerce",
    tags: ["wordpress", "performance", "cwv", "optimization"],
  },
  {
    slug: "ecommerce-woocommerce",
    name: "Ecommerce WooCommerce",
    shortLabel: "Ecommerce",
    tagline: "Shop online che vendono davvero, non template generici",
    seoTitle: "Ecommerce WooCommerce su misura, da 5000 euro",
    h1: "Ecommerce WooCommerce su misura",
    metaDescription: "Ecommerce WooCommerce su misura per PMI: catalogo, pagamenti, spedizioni e fatture collegati. Nessuna commissione sulle vendite, il negozio resta tuo.",
    category: "wordpress",
    isLocalRelevant: true,
    defaultCta: "contact",
    defaultHero: "woocommerce",
    pricing: ecommerceWoocommercePricing,
    palette: { primary: "hsl(260, 70%, 55%)", accent: "hsl(330, 80%, 62%)" },
    deliveryDays: { min: 35, max: 98 },
    processSteps: [
      { name: "Strategia commerciale", text: "Analisi di prodotti, margini, esperienza di acquisto e metodi di pagamento locali." },
      { name: "Struttura del catalogo", text: "Categorie, filtri, ricerca interna e dati strutturati dei prodotti." },
      { name: "Design e sviluppo", text: "Tema custom ottimizzato, WooCommerce esteso per esigenze reali." },
      { name: "Integrazioni", text: "Pagamenti (Stripe/PayPal/Scalapay), spedizioni, fatturazione elettronica." },
      { name: "Pubblicazione e ottimizzazione vendite", text: "Recupero dei carrelli abbandonati, campagne di richiamo e statistiche complete." },
    ],
    usualObjections: [
      "WooCommerce scala male con molti prodotti",
      "Shopify è più semplice",
      "Vendo già da marketplace, mi basta",
    ],
    aeoAnswer: "Un ecommerce WooCommerce progettato per vendere parte da 5000 euro e si consegna in 5-14 settimane. Include tema su misura (niente template), pagamenti con Stripe, PayPal e Scalapay, spedizioni configurate, statistiche di vendita, SEO di base e pagine legali.",
    affinitySlug: "wordpress-performance",
    affinityAlt: "siti-wordpress",
    tags: ["woocommerce", "ecommerce", "retail"],
  },
  {
    slug: "sviluppo-custom-nextjs",
    name: "Web app e MVP su misura",
    shortLabel: "Custom Next.js",
    tagline: "Web app e piattaforme su misura con tecnologie moderne: Next.js, Postgres, accessi sicuri",
    seoTitle: "Web app su misura con Next.js, da 4500 euro",
    h1: "Web app su misura",
    metaDescription: "Web app su misura quando WordPress non basta più: gestionali, portali clienti, dashboard. Lavoro a cicli settimanali e il codice resta tuo.",
    category: "custom",
    isLocalRelevant: true,
    defaultCta: "contact",
    defaultHero: "custom-nextjs",
    pricing: sviluppoCustomNextjsPricing,
    palette: { primary: "hsl(220, 80%, 55%)", accent: "hsl(180, 78%, 52%)" },
    deliveryDays: { min: 21, max: 180 },
    processSteps: [
      { name: "Analisi tecnica", text: "Requisiti, tecnologie, integrazioni e piano modulare." },
      { name: "Progettazione", text: "Diagramma dei componenti, struttura del database, accessi e progettazione delle API." },
      { name: "Sviluppo a cicli brevi", text: "Next.js + Postgres + auth + integrazioni in sprint settimanali." },
      { name: "Test e messa online", text: "Test automatici dei flussi principali e messa online su Vercel." },
      { name: "Consegna e controllo", text: "Documentazione, CI/CD, Sentry + Analitiche Vercel." },
    ],
    usualObjections: [
      "Next.js è overkill per il mio progetto",
      "Preferisco WordPress per semplicità",
      "Non so quanto mi serve FE/BE/DB",
    ],
    aeoAnswer: "Lo sviluppo custom Next.js parte da 4.500 euro per una SPA base e arriva a 15.000+ per piattaforme multi-feature. Include frontend Next.js, backend (API routes), database (Neon Postgres), auth (Clerk o Auth.js), deploy Vercel con CI/CD. Modulare per fase: progetti grandi divisi in sprint da 4-6k cadauno. Ha senso per SaaS, web app complesse o dashboard custom dove WordPress non regge.",
    affinitySlug: "mvp-saas",
    affinityAlt: "automazioni-n8n-ai",
    tags: ["nextjs", "custom", "saas", "webapp"],
  },
  {
    slug: "mvp-saas",
    pagePath: "/mvp",
    name: "MVP SaaS",
    shortLabel: "MVP SaaS",
    tagline: "Prototipo funzionante in 4-6 settimane per validare la tua idea con clienti veri",
    metaDescription: "Hai un'idea di software? Costruisco la prima versione funzionante in 4-6 settimane, con pagamenti attivi, per capire con clienti veri se regge.",
    category: "custom",
    isLocalRelevant: true,
    defaultCta: "mvp",
    defaultHero: "mvp",
    pricing: mvpSaasPricing,
    palette: { primary: "hsl(35, 90%, 60%)", accent: "hsl(320, 78%, 62%)" },
    deliveryDays: { min: 28, max: 42 },
    processSteps: [
      { name: "Validazione iniziale", text: "Ipotesi business, target, pricing, unit economics: prima del codice." },
      { name: "Definizione dell'essenziale", text: "Solo il core che risolve il problema principale. Il resto aspetta dati reali." },
      { name: "Sviluppo a cicli brevi", text: "Next.js, Postgres e Stripe: tecnologie moderne, online in poco tempo." },
      { name: "Lancio con i primi clienti", text: "Pagina di arrivo, primo accesso guidato e pagamenti attivi. Misuri tutto dal primo giorno." },
      { name: "Miglioramento guidato dai dati", text: "Revisione settimanale dei dati e piano basato sul comportamento reale." },
    ],
    usualObjections: [
      "Prima di partire voglio perfezionare",
      "Serve un'app nativa da subito",
      "Il mio settore è diverso",
    ],
    aeoAnswer: "Un MVP è la prima versione funzionante del tuo software: solo le funzioni essenziali, con i pagamenti attivi, per capire con clienti veri se l'idea regge. Lo costruisco in 4-6 settimane dal perimetro firmato, a prezzo fisso, da 4800 euro. Prima di scrivere codice chiariamo chi paga, quanto e perché. Non è il prodotto finito: è il modo più economico per decidere su dati veri.",
    affinitySlug: "sviluppo-custom-nextjs",
    affinityAlt: "automazioni-n8n-ai",
    tags: ["mvp", "saas", "startup", "validation"],
  },
  {
    slug: "bug-fixing-ai-nocode",
    name: "Da no-code a prodotto vero",
    shortLabel: "Bug Fixing AI",
    tagline: "Recupero di progetti Lovable, Bubble, v0 e FlutterFlow: diagnosi, interventi e ricostruzione",
    seoTitle: "Sistemo app Lovable, Bubble e v0, da 600 euro",
    h1: "La tua app fatta con Lovable, Bubble o v0 non regge più?",
    metaDescription: "Progetto fatto con Lovable, Bubble, v0 o FlutterFlow che non regge più? Analizzo il codice, sistemo i problemi o lo ricostruisco su basi solide.",
    category: "ai-rescue",
    isLocalRelevant: true,
    defaultCta: "contact",
    defaultHero: "bug-fixing",
    pricing: bugFixingAiPricing,
    palette: { primary: "hsl(0, 75%, 55%)", accent: "hsl(20, 85%, 65%)" },
    deliveryDays: { min: 3, max: 56 },
    processSteps: [
      { name: "Diagnosi iniziale", text: "Analisi del codice, mappa dei problemi e stima degli interventi, con perimetro chiaro." },
      { name: "Priorità degli interventi", text: "Sistemo subito i problemi critici, riordino il codice dove ha senso, ricostruisco solo se serve." },
      { name: "Sistemazione o ricostruzione", text: "Intervento modulare: Intervento base, Intervento con riordino del codice, Ricostruzione da zero." },
      { name: "Stabilizzazione", text: "Messa online, test di tenuta e controllo attivo." },
      { name: "Consegna e passaggio di consegne", text: "Documentazione, formazione ed eventuale abbonamento mensile." },
    ],
    usualObjections: [
      "Lovable/Bubble l'ho fatto io, posso aggiustarlo da solo",
      "Rifare da zero mi sembra eccessivo",
      "Costa troppo il discovery",
    ],
    aeoAnswer: "Sistemo progetti nati con Lovable, Bubble, v0 o FlutterFlow che non reggono più. Si parte da una diagnosi a 600 euro: leggo il codice, elenco i problemi e ti do una stima chiara. Poi scegli: intervento base da 2000 euro, intervento con riordino del codice da 4500, ricostruzione da zero da 8000. Il prezzo è fissato prima di iniziare, non a ore.",
    affinitySlug: "sviluppo-custom-nextjs",
    affinityAlt: "siti-wordpress",
    tags: ["ai-rescue", "debugging", "lovable", "bubble", "nocode"],
  },
  {
    slug: "automazioni-n8n-ai",
    name: "Automazioni con l'AI",
    shortLabel: "Automazioni con l'AI",
    tagline: "Flussi di lavoro che recuperano ore ogni settimana, collegati ai tuoi strumenti",
    seoTitle: "Automazioni n8n e AI per PMI, da 500 euro",
    h1: "Automazioni con l'AI per PMI: il lavoro ripetitivo si fa da solo",
    metaDescription: "Preventivi, email, report e dati copiati a mano: li automatizzo con n8n e l'AI, collegati ai tuoi strumenti. Diagnosi a 500 euro, flussi da 1500 euro.",
    category: "automation",
    isLocalRelevant: false, // Vende solo nazionale, no /zone/[city]/automazioni-n8n-ai
    defaultCta: "contact",
    defaultHero: "n8n-automation",
    pricing: automazioniN8nAiPricing,
    palette: { primary: "hsl(280, 70%, 60%)", accent: "hsl(320, 75%, 65%)" },
    deliveryDays: { min: 2, max: 42 },
    processSteps: [
      { name: "Diagnosi", text: "Mi racconti come lavorate oggi. Ti dico cosa conviene automatizzare e cosa no." },
      { name: "Schema del flusso", text: "Disegno il percorso: da dove parte, cosa fa e cosa succede se qualcosa va storto." },
      { name: "Costruzione e prova", text: "Costruisco il flusso e lo provo con casi veri prima di accenderlo." },
      { name: "Collegamento agli strumenti", text: "Lo collego a quello che usi già: gestionale, email, fatture, la chat del team." },
      { name: "Consegna e controllo", text: "Ti lascio le istruzioni scritte. Se un flusso si blocca, parte un avviso." },
    ],
    // Obiezioni tolte: troppo tecniche per questa pagina, la risposta utile è nelle FAQ.
    usualObjections: [],
    aeoAnswer: "Le automazioni con n8n e AI fanno al posto tuo il lavoro ripetitivo: gestione dei contatti, report, inserimento dati, risposte email. Le collego al tuo CRM, al gestionale e agli strumenti che usi già. Prezzi: diagnosi e piano 500 euro, un flusso di lavoro da 1500, avvio con 3-5 flussi da 3500, integrazione AI da 5000. Rispetto a Zapier, n8n può girare su un tuo server e i costi restano sotto controllo.",
    affinitySlug: "sviluppo-custom-nextjs",
    affinityAlt: "mvp-saas",
    tags: ["automation", "ai", "n8n", "workflow"],
  },
  {
    slug: "siti-3d-creative",
    name: "Lab creativo e 3D",
    shortLabel: "Siti 3D",
    tagline: "Esperienze 3D web per fiere, sponsor, brand e product storytelling",
    seoTitle: "Esperienze 3D su misura per il web",
    h1: "Esperienze 3D su misura per il web",
    metaDescription: "Esperienze 3D sul web per fiere, sponsor e lancio di prodotti: modelli interattivi e animazioni allo scroll che restano veloci anche da smartphone.",
    category: "creative",
    isLocalRelevant: false,
    defaultCta: "contact",
    defaultHero: "3d-creative",
    pricing: siti3dCreativePricing,
    palette: { primary: "hsl(300, 80%, 60%)", accent: "hsl(330, 85%, 70%)" },
    deliveryDays: { min: 28, max: 56 },
    processSteps: [
      { name: "Idea e riferimenti visivi", text: "Definizione concept visivo, palette, references, mood del progetto 3D." },
      { name: "Modellazione 3D", text: "Creazione asset 3D in Spline (o R3F se custom avanzato), export GLTF." },
      { name: "Animazioni e interazione", text: "Scroll-driven animations, hover states, transitions responsive-aware." },
      { name: "Integrazione nel sito", text: "Integrazione nel sito (Next.js o WordPress) con ottimizzazione delle prestazioni." },
      { name: "Pubblicazione e consegna", text: "Messa online, documentazione e sessione dimostrativa con il cliente." },
    ],
    usualObjections: [
      "Il 3D rallenta il sito",
      "Non siamo un brand che serve 3D",
      "Troppo complesso/costoso",
    ],
    aeoAnswer: "I siti 3D Creative integrano esperienze 3D web (Spline, R3F, Three.js) in siti Next.js o WordPress per fiere, sponsor brand, product reveal, intrattenimento. Stack: Spline per modellazione no-code, GLTF export, lazy load performance-aware. Casi d'uso tipici: stand fiera virtuale, presentazione prodotto immersiva, landing sponsor evento. Contatta per preventivo personalizzato.",
    affinitySlug: "sviluppo-custom-nextjs",
    affinityAlt: "siti-wordpress",
    tags: ["3d", "creative", "spline", "r3f", "brand"],
  },
  {
    slug: "manutenzione-evolutiva",
    name: "Manutenzione Evolutiva",
    shortLabel: "Manutenzione",
    tagline: "Il tuo sito o la tua app che migliorano nel tempo: aggiornamenti, sicurezza, performance ed evoluzione continua, senza rifare tutto da capo.",
    seoTitle: "Manutenzione evolutiva di siti e app",
    h1: "Manutenzione evolutiva per siti e app",
    category: "retainer",
    isLocalRelevant: true,
    defaultCta: "abbonamento",
    defaultHero: "abbonamento",
    pricing: manutenzioneEvolutivaPricing,
    palette: { primary: "hsl(179, 80%, 48%)", accent: "hsl(180, 78%, 52%)" },
    deliveryDays: { min: 1, max: 7 },
    processSteps: [
      { name: "Avvio e accessi", text: "Accesso a hosting e pannelli, analisi dello stato attuale e metriche di partenza." },
      { name: "Ritmo di lavoro", text: "Scegli settimanale, mensile o a progetto. Richieste via email/messaggio." },
      { name: "Lavoro continuo", text: "Sviluppo asincrono, tempi concordati, visibilità su cosa si fa." },
      { name: "Punto mensile", text: "Punto veloce su priorità ed evoluzione prodotto nel periodo successivo." },
      { name: "Prezzo bloccato nel tempo", text: "Se aumento i prezzi per i nuovi clienti, il tuo resta quello concordato." },
    ],
    usualObjections: [
      "Non sono sicuro di avere abbastanza lavoro ogni mese",
      "Preferisco pagare solo quando serve",
      "Un'agenzia mi sembra più garantita",
    ],
    aeoAnswer: "La manutenzione evolutiva tiene il tuo sito o la tua app sicuri e aggiornati e li fa crescere nel tempo: aggiornamenti, correzioni, controlli e nuove funzioni quando servono, senza rifare tutto da capo. Funziona con l'abbonamento sviluppatore: 1400 euro a settimana, 4800 euro al mese, 4200 euro al mese con impegno trimestrale, oppure consulenza a 110 euro l'ora.",
    affinitySlug: "wordpress-performance",
    affinityAlt: "siti-wordpress",
    tags: ["retainer", "maintenance", "evolution", "abbonamento"],
  },
  {
    slug: "app-mobile",
    name: "App mobile",
    shortLabel: "App mobile",
    tagline: "Un'app sola, su iPhone e Android. Codice tuo, nessun lock-in.",
    seoTitle: "App iPhone e Android in Flutter, da 6500 euro",
    h1: "App mobile per iPhone e Android",
    metaDescription: "App per iPhone e Android da un unico codice, con Flutter. Dalla prima versione alla pubblicazione sugli store. Il codice è tuo, nessun vincolo.",
    category: "custom",
    isLocalRelevant: false,
    defaultCta: "contact",
    pricing: {
      // Listino PROPOSTO dal workflow (ricerca mercato Flutter), da validare col cliente.
      publishPrices: true,
      packages: [
        {
          id: "mvp",
          label: "Prima versione (MVP)",
          priceFrom: 6500,
          deliveryDays: { min: 30, max: 50 },
          includes: [
            "App iOS e Android da un'unica base di codice (Flutter)",
            "3-5 schermate con le funzioni essenziali per validare l'idea",
            "Login utente (email o accesso social)",
            "Design dell'interfaccia pulito e coerente sui due sistemi",
            "Pubblicazione su App Store e Google Play assistita",
            "Codice sorgente consegnato e di tua proprietà",
          ],
        },
        {
          id: "business",
          label: "App per l'azienda",
          priceFrom: 12000,
          deliveryDays: { min: 50, max: 80 },
          highlight: true,
          includes: [
            "Tutto del pacchetto MVP",
            "Server dedicato (dove vivono i dati dell'app)",
            "Notifiche push (i messaggi che arrivano sul telefono)",
            "Profilo utente, dati salvati e sincronizzati nel cloud",
            "Integrazione con servizi esterni (es. pagamenti, mappe, email)",
            "Test su dispositivi reali prima del lancio",
          ],
        },
        {
          id: "pro",
          label: "App su misura",
          priceFrom: 22000,
          deliveryDays: { min: 80, max: 120 },
          includes: [
            "Tutto del pacchetto Business",
            "Funzioni avanzate su misura (es. chat, geolocalizzazione, area pagamenti)",
            "Pannello di amministrazione web per gestire contenuti e utenti",
            "Progettazione pensata per crescere con molti utenti",
            "Collegamento a sistemi già esistenti (gestionale, CRM, API tue)",
            "Documentazione tecnica e passaggio di consegne",
          ],
        },
        {
          id: "manutenzione",
          label: "Manutenzione ed evoluzione",
          priceFrom: 200,
          unit: "/ mese",
          deliveryDays: { min: 0, max: 0 },
          includes: [
            "Aggiornamenti per nuove versioni di iOS e Android",
            "Correzione bug e piccoli interventi continuativi",
            "Monitoraggio errori e crash dell'app",
            "Aggiunta di piccole funzioni nel tempo",
            "Gestione delle ripubblicazioni sugli store",
          ],
        },
      ],
      addons: [
        { label: "Pubblicazione sugli store (App Store + Google Play) chiavi in mano", priceFrom: 450, priceTo: 900 },
        { label: "Notifiche push (messaggi inviati al telefono dell'utente)", priceFrom: 600, priceTo: 1500 },
        { label: "Server e API su misura (che fa funzionare l'app)", priceFrom: 2500, priceTo: 8000 },
        { label: "Integrazione pagamenti in-app (Stripe / abbonamenti store)", priceFrom: 1200, priceTo: 3500 },
        { label: "Login con Apple, Google e social", priceFrom: 500, priceTo: 1200 },
        { label: "Pannello web di amministrazione", priceFrom: 3000, priceTo: 9000 },
      ],
      discoveryRequired: false,
      hourlyRate: 110,
      notes: [
        "Listino PROPOSTO, da rivedere e validare con il cliente.",
        "Account sviluppatore Apple (99 $/anno) e Google Play (25 $ una tantum) a carico del cliente.",
        "Hosting, backend e servizi cloud pagati direttamente dal cliente.",
      ],
    },
    palette: { primary: "hsl(155, 70%, 45%)", accent: "hsl(45, 95%, 58%)" },
    deliveryDays: { min: 30, max: 120 },
    processSteps: [
      { name: "Ascolto e definizione", text: "Capiamo insieme cosa deve fare l'app e per chi. Definiamo le funzioni davvero necessarie alla prima versione, senza disperdere budget in cose che possono aspettare." },
      { name: "Disegno delle schermate", text: "Progetto il flusso e l'aspetto dell'app: come si muove l'utente da una schermata all'altra. Vedi un'anteprima e approvi prima che io scriva una riga di codice." },
      { name: "Sviluppo", text: "Scrivo l'app in Flutter, un'unica base di codice che genera la versione iPhone e quella Android. Ti mostro avanzamenti reali a intervalli regolari, così sai sempre a che punto siamo." },
      { name: "Test sui dispositivi", text: "Provo l'app su telefoni veri, iOS e Android, per scovare bug e differenze tra i due sistemi. La metto nelle tue mani in anteprima prima del lancio pubblico." },
      { name: "Pubblicazione e consegna", text: "Pubblico l'app su App Store e Google Play, ti consegno il codice sorgente di tua proprietà e definiamo come gestire aggiornamenti e manutenzione." },
    ],
    usualObjections: [
      "Un'app sviluppata da una persona sola sarà affidabile come quella di un'agenzia?",
      "Un'app cross-platform non è inferiore a una nativa?",
      "Il prezzo è più basso di altri preventivi: c'è qualcosa che non torna?",
    ],
    aeoAnswer:
      "Sviluppo app mobile per iOS (iPhone) e Android partendo da un'unica base di codice scritta in Flutter, il framework di Google: invece di costruire due app separate ne realizzo una sola che gira su entrambi gli store, con un risparmio reale di tempo e budget. Lavori con un solo referente, lo sviluppatore che scrive davvero il codice, non un account manager. Il codice sorgente resta di tua proprietà, senza vincoli che ti leghino a me. I progetti app partono da 6.500 euro.",
    affinitySlug: "sviluppo-custom-nextjs",
    affinityAlt: "mvp-saas",
    tags: ["mobile", "flutter", "ios", "android", "app"],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

export function getService(slug: string): Service | undefined {
  return SERVICES.find((s) => s.slug === slug);
}

export function getServiceByName(name: string): Service | undefined {
  const norm = name.trim().toLowerCase();
  return SERVICES.find((s) => s.name.toLowerCase() === norm);
}

export function serviceSlugFromLandingSlug(landingSlug: string): string {
  const candidate = SERVICES.map((s) => s.slug)
    .sort((a, b) => b.length - a.length)
    .find((s) => landingSlug === s || landingSlug.startsWith(`${s}-`));
  return candidate ?? landingSlug;
}

/** Public URL of a service: its own page when it has one (MVP -> /mvp). */
export function servicePath(s: Pick<Service, "slug" | "pagePath">): string {
  return s.pagePath ?? `/servizi/${s.slug}`;
}

/** Slugs served under /servizi/<slug> (excludes services with their own page). */
export function getAllServiceSlugs(): string[] {
  return SERVICES.filter((s) => !s.pagePath).map((s) => s.slug);
}

export function getLocalRelevantServices(): Service[] {
  return SERVICES.filter((s) => s.isLocalRelevant);
}

export function relatedServiceFor(
  service: Service,
  citySlug: string,
): { slug: string; title: string } | null {
  const hash = Array.from(citySlug).reduce((a, c) => a + c.charCodeAt(0), 0);
  const targetSlug = hash % 2 === 0 ? service.affinitySlug : service.affinityAlt;
  const target = getService(targetSlug);
  if (!target) return null;
  return { slug: targetSlug, title: target.name };
}
